import { Popover, PopoverContent } from "@/components/ui/popover";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { QueryVehiclesOrderByColumn, SortOrder } from "@/gql/graphql";
import { Fragment, useState } from "react";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { keepPreviousData } from "@tanstack/react-query";
import { vehicleListQuery } from "@/app/Vehicles/GraphQL/vehicleListQuery";
import { vehicleNameQuery } from "@/app/Vehicles/GraphQL/vehicleNameQuery";
import { TableFilterButton } from "@/lib/Components/DataTable/Filters/TableFilterButton";

export function VehicleFilter({
  placeholder,
  onChange,
  selected,
}: {
  placeholder: string;
  selected: string[];
  onChange: (selected: string[]) => void;
}) {
  const { hasSupplier } = useGlobalSupplier();
  const numSelected = selected.length;
  const selectedLabel =
    numSelected > 2 ? (
      <>
        <VehicleLabel id={selected[0]} /> and {numSelected - 1} more
      </>
    ) : (
      selected.map((id, index) => (
        <Fragment key={id}>
          {index > 0 ? ", " : null}
          <VehicleLabel id={id} />
        </Fragment>
      ))
    );

  const [search, setSearch] = useState("");
  const { supplierId } = useGlobalSupplier();
  const { data } = useGqlQuery(
    vehicleListQuery,
    {
      first: 20,
      page: 1,
      archived: false,
      search,
      supplier_id: supplierId ?? undefined,
      orderBy: !search
        ? [
            {
              order: SortOrder.Asc,
              column: QueryVehiclesOrderByColumn.Name,
            },
          ]
        : undefined,
    },
    {
      placeholderData: keepPreviousData,
    },
  );

  return (
    <Popover>
      <TableFilterButton
        label={placeholder}
        selectedLabel={selectedLabel}
        numSelected={numSelected}
        onClear={() => {
          onChange([]);
        }}
      />
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search vehicles..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No vehicles found.</CommandEmpty>
            <CommandGroup>
              {data?.vehicles.data.map((vehicle) => {
                const isSelected = selected.includes(vehicle.id);

                return (
                  <CommandItem
                    key={vehicle.id}
                    value={vehicle.id}
                    onSelect={(currentValue) => {
                      onChange(
                        selected.includes(currentValue)
                          ? selected.filter((v) => v !== currentValue)
                          : [...selected, currentValue],
                      );
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <div>
                      <p>{vehicle.name}</p>
                      {!hasSupplier ? (
                        <p className="text-xs text-gray-500">
                          {vehicle.supplier.name}
                        </p>
                      ) : null}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function VehicleLabel({ id }: { id: string }) {
  const { data } = useGqlQuery(vehicleNameQuery, { id });

  return data?.vehicle.name ?? null;
}
